/**
 * Refine Data Provider for AxonBOS Gateway → axon-data service.
 *
 * Routes all CRUD operations through the axon-gateway proxy:
 *   /v1/data/tables/browse   — list/search records (POST)
 *   /v1/data/tables/records  — create (POST), update (PUT)
 *   /v1/data/tables/records/:connector_id/:table/:id — delete (DELETE)
 *
 * The gateway injects X-Company-ID, X-User-ID, X-User-Role from JWT,
 * so multi-tenancy is handled server-side — no client-side org_id injection.
 *
 * connector_id identifies which data source (HROS, Safal, etc.) to query.
 * Resolved from: meta.connectorId > localStorage > default (1).
 *
 * API Response Envelope: { success, data, error: { code, message }, meta }
 */

import type {
  DataProvider,
  GetListParams,
  GetListResponse,
  GetOneParams,
  GetOneResponse,
  CreateParams,
  CreateResponse,
  UpdateParams,
  UpdateResponse,
  DeleteOneParams,
  DeleteOneResponse,
  GetManyParams,
  GetManyResponse,
  BaseRecord,
  HttpError,
} from '@refinedev/core';
import axios, { type AxiosInstance, type AxiosError } from 'axios';
import { getAccessToken } from './auth-provider';

// ─── Constants ────────────────────────────────────────────────────

const CONNECTOR_ID_KEY = 'axon_connector_id';
const DEFAULT_CONNECTOR_ID = 1;

// ─── Types ────────────────────────────────────────────────────────

/** API response envelope from axon-gateway / axon-data */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: { code: string; message: string } | null;
  meta: {
    request_id: string;
    company_id?: number;
    page?: number;
    total?: number;
    limit?: number;
    [key: string]: unknown;
  };
}

/** Browse request body for POST /v1/data/tables/browse */
interface BrowseRequest {
  connector_id: number;
  table_name: string;
  page?: number;
  page_size?: number;
  sort_column?: string;
  sort_direction?: 'asc' | 'desc';
  filters?: Record<string, unknown>;
  search?: string;
}

/** Record mutation body for POST/PUT /v1/data/tables/records */
interface RecordCreateBody {
  connector_id: number;
  table_name: string;
  data: Record<string, unknown>;
}

interface RecordUpdateBody {
  connector_id: number;
  table_name: string;
  row_id: string;
  data: Record<string, unknown>;
}

/** Extended meta that Refine passes through */
interface AxonMeta {
  connectorId?: number;
  connectorType?: string;
  fields?: string[];
  search?: string;
  [key: string]: unknown;
}

// ─── Helpers ──────────────────────────────────────────────────────

/** Resolve connector_id: meta override > localStorage > default */
function resolveConnectorId(meta?: AxonMeta): number {
  if (meta?.connectorId) return meta.connectorId;

  const stored = localStorage.getItem(CONNECTOR_ID_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed)) return parsed;
  }

  return DEFAULT_CONNECTOR_ID;
}

/** Set the active connector ID in localStorage */
export function setConnectorId(id: number): void {
  localStorage.setItem(CONNECTOR_ID_KEY, String(id));
}

/** Get the active connector ID from localStorage */
export function getConnectorId(): number {
  const stored = localStorage.getItem(CONNECTOR_ID_KEY);
  return stored ? parseInt(stored, 10) || DEFAULT_CONNECTOR_ID : DEFAULT_CONNECTOR_ID;
}

// ─── Filter Mapping ───────────────────────────────────────────────

/**
 * Map Refine CrudFilter operators → axon-data filter format.
 *
 * axon-data accepts: { column_name: "value" } for equality,
 * or { column_name: "value%" } for LIKE, etc.
 * Complex filters use: { column_name: { operator: value } }
 */
interface CrudFilter {
  field: string;
  operator: string;
  value: unknown;
}

interface LogicalFilter {
  operator: 'and' | 'or';
  value: CrudFilter[];
}

type Filter = CrudFilter | LogicalFilter;

function buildAxonFilters(filters?: Filter[]): Record<string, unknown> {
  if (!filters || filters.length === 0) return {};

  const result: Record<string, unknown> = {};

  for (const filter of filters) {
    if ('field' in filter) {
      const { field, operator, value } = filter;

      switch (operator) {
        case 'eq':
          result[field] = value;
          break;
        case 'ne':
          result[field] = { ne: value };
          break;
        case 'lt':
          result[field] = { lt: value };
          break;
        case 'gt':
          result[field] = { gt: value };
          break;
        case 'lte':
          result[field] = { lte: value };
          break;
        case 'gte':
          result[field] = { gte: value };
          break;
        case 'contains':
        case 'containss':
          result[field] = `%${value}%`;
          break;
        case 'startswith':
          result[field] = `${value}%`;
          break;
        case 'endswith':
          result[field] = `%${value}`;
          break;
        case 'null':
          result[field] = { is_null: true };
          break;
        case 'nnull':
          result[field] = { is_null: false };
          break;
        case 'in':
          result[field] = { in: value };
          break;
        case 'nin':
          result[field] = { nin: value };
          break;
        case 'between':
          result[field] = { between: value };
          break;
        default:
          // Pass through unknown operators
          result[field] = { [operator]: value };
      }
    }
    // LogicalFilter (and/or) — flatten into individual field filters
    // axon-data doesn't support nested logical operators yet,
    // so we merge all sub-filters into the top level
    else if ('value' in filter && Array.isArray(filter.value)) {
      const subFilters = buildAxonFilters(filter.value as CrudFilter[]);
      Object.assign(result, subFilters);
    }
  }

  return result;
}

// ─── Error Mapper ─────────────────────────────────────────────────

function mapAxonError(error: AxiosError): HttpError {
  const data = error.response?.data as ApiResponse<unknown> | undefined;
  const status = error.response?.status ?? 500;

  const message = data?.error?.message ?? error.message ?? 'Unknown error';
  const code = data?.error?.code ?? 'UNKNOWN';

  return {
    message,
    statusCode: status,
    errors: [{ key: code, message }],
  } as HttpError;
}

// ─── Token Refresh State ─────────────────────────────────────────
// Shared across all axios instances so only one refresh runs at a time.

const TOKEN_KEY = 'axon_token';
const REFRESH_TOKEN_KEY = 'axon_refresh_token';

let _refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(gatewayUrl: string): Promise<string | null> {
  // Deduplicate: if a refresh is already in progress, share its result
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;

    try {
      const { data: response } = await axios.post(
        `${gatewayUrl}/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (response.success && response.data?.access_token) {
        localStorage.setItem(TOKEN_KEY, response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refresh_token);
        }
        return response.data.access_token as string;
      }
    } catch {
      // Refresh failed — clear tokens so next request triggers re-login
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } finally {
      _refreshPromise = null;
    }
    return null;
  })();

  return _refreshPromise;
}

// ─── Axios Instance Factory ───────────────────────────────────────

function createAxiosInstance(gatewayUrl: string): AxiosInstance {
  const instance = axios.create({
    baseURL: gatewayUrl,
    headers: { 'Content-Type': 'application/json' },
  });

  // Attach JWT token to every request
  instance.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Auto-refresh on 401 — retry once with a fresh token
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      if (error.response?.status === 401 && !originalRequest._retried) {
        originalRequest._retried = true;

        const newToken = await refreshAccessToken(gatewayUrl);
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        }

        // Refresh failed → clear tokens and redirect to login
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }

      const httpError = mapAxonError(error);
      return Promise.reject(httpError);
    },
  );

  return instance;
}

// ─── Data Provider ────────────────────────────────────────────────

export function createDataProvider(gatewayUrl: string): DataProvider {
  const httpClient = createAxiosInstance(gatewayUrl);

  return {
    /**
     * POST /v1/data/tables/browse — List with filter, sort, pagination
     *
     * axon-data expects POST body: {
     *   connector_id, table_name, page, page_size,
     *   sort_column, sort_direction, filters, search
     * }
     */
    getList: async <TData extends BaseRecord = BaseRecord>({
      resource,
      pagination,
      filters,
      sorters,
      meta,
    }: GetListParams): Promise<GetListResponse<TData>> => {
      const { current = 1, pageSize = 25, mode = 'server' } = pagination ?? {};
      const axonMeta = meta as AxonMeta | undefined;
      const connectorId = resolveConnectorId(axonMeta);

      const body: BrowseRequest = {
        connector_id: connectorId,
        table_name: resource,
      };

      // Pagination
      if (mode === 'server') {
        body.page = current;
        body.page_size = pageSize;
      }

      // Sorting — use first sorter (axon-data supports single sort)
      if (sorters && sorters.length > 0) {
        body.sort_column = sorters[0].field;
        body.sort_direction = sorters[0].order;
      }

      // Filters
      const axonFilters = buildAxonFilters(filters as Filter[]);
      if (Object.keys(axonFilters).length > 0) {
        body.filters = axonFilters;
      }

      // Free-text search
      if (axonMeta?.search) {
        body.search = axonMeta.search;
      }

      const { data: response } = await httpClient.post<ApiResponse<TData[]>>(
        '/v1/data/tables/browse',
        body,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to fetch records',
          statusCode: 400,
        } as HttpError;
      }

      return {
        data: response.data ?? [],
        total: response.meta?.total ?? response.data?.length ?? 0,
      };
    },

    /**
     * GET single record — uses browse with id filter
     * (axon-data doesn't have a dedicated getOne endpoint)
     */
    getOne: async <TData extends BaseRecord = BaseRecord>({
      resource,
      id,
      meta,
    }: GetOneParams): Promise<GetOneResponse<TData>> => {
      const axonMeta = meta as AxonMeta | undefined;
      const connectorId = resolveConnectorId(axonMeta);

      const body: BrowseRequest = {
        connector_id: connectorId,
        table_name: resource,
        page: 1,
        page_size: 1,
        filters: { id: id },
      };

      const { data: response } = await httpClient.post<ApiResponse<TData[]>>(
        '/v1/data/tables/browse',
        body,
      );

      if (!response.success || !response.data?.length) {
        throw {
          message: response.error?.message ?? `Record ${id} not found`,
          statusCode: 404,
        } as HttpError;
      }

      return { data: response.data[0] };
    },

    /**
     * POST /v1/data/tables/records — Create record
     */
    create: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
      resource,
      variables,
      meta,
    }: CreateParams<TVariables>): Promise<CreateResponse<TData>> => {
      const axonMeta = meta as AxonMeta | undefined;
      const connectorId = resolveConnectorId(axonMeta);

      const body: RecordCreateBody = {
        connector_id: connectorId,
        table_name: resource,
        data: variables as Record<string, unknown>,
      };

      const { data: response } = await httpClient.post<ApiResponse<TData>>(
        '/v1/data/tables/records',
        body,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to create record',
          statusCode: 400,
        } as HttpError;
      }

      return { data: response.data };
    },

    /**
     * PUT /v1/data/tables/records — Update record
     */
    update: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
      resource,
      id,
      variables,
      meta,
    }: UpdateParams<TVariables>): Promise<UpdateResponse<TData>> => {
      const axonMeta = meta as AxonMeta | undefined;
      const connectorId = resolveConnectorId(axonMeta);

      const body: RecordUpdateBody = {
        connector_id: connectorId,
        table_name: resource,
        row_id: String(id),
        data: variables as Record<string, unknown>,
      };

      const { data: response } = await httpClient.put<ApiResponse<TData>>(
        '/v1/data/tables/records',
        body,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to update record',
          statusCode: 400,
        } as HttpError;
      }

      return { data: response.data };
    },

    /**
     * DELETE /v1/data/tables/records/:connector_id/:table/:id
     */
    deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
      resource,
      id,
      meta,
    }: DeleteOneParams<TVariables>): Promise<DeleteOneResponse<TData>> => {
      const axonMeta = meta as AxonMeta | undefined;
      const connectorId = resolveConnectorId(axonMeta);

      const { data: response } = await httpClient.delete<ApiResponse<unknown>>(
        `/v1/data/tables/records/${connectorId}/${resource}/${id}`,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to delete record',
          statusCode: 400,
        } as HttpError;
      }

      return { data: { id } as unknown as TData };
    },

    /**
     * Bulk fetch by IDs — uses browse with id IN filter
     */
    getMany: async <TData extends BaseRecord = BaseRecord>({
      resource,
      ids,
      meta,
    }: GetManyParams): Promise<GetManyResponse<TData>> => {
      const axonMeta = meta as AxonMeta | undefined;
      const connectorId = resolveConnectorId(axonMeta);

      const body: BrowseRequest = {
        connector_id: connectorId,
        table_name: resource,
        page_size: ids.length,
        filters: { id: { in: ids } },
      };

      const { data: response } = await httpClient.post<ApiResponse<TData[]>>(
        '/v1/data/tables/browse',
        body,
      );

      return { data: response.data ?? [] };
    },

    /**
     * Return the gateway base URL
     */
    getApiUrl: () => gatewayUrl,

    /**
     * Custom method — expose raw httpClient for advanced queries
     * (e.g., schema endpoints, intelligence endpoints, etc.)
     */
    custom: async ({ url, method = 'get', payload, query, headers }) => {
      const { data } = await httpClient({
        url,
        method,
        data: payload,
        params: query,
        headers,
      });

      return { data };
    },
  };
}

// ─── HROS Data Provider ───────────────────────────────────────────

/**
 * Maps Refine resource names to HROS BLL path segments.
 * The BLL is mounted at /v1/hros/ with REST endpoints.
 */
const HROS_RESOURCE_PATH: Record<string, string> = {
  employees: 'employees',
  departments: 'departments',
  designations: 'designations',
  leave_types: 'leave/types',
  leave_requests: 'leave/requests',
  attendance_logs: 'attendance',
  shifts: 'shifts',
  goals: 'goals',
  time_logs: 'time-logs',
  projects: 'projects',
  timesheets: 'timesheets',
};

function resolveHROSPath(resource: string): string {
  return HROS_RESOURCE_PATH[resource] ?? resource.replace(/_/g, '-');
}

/**
 * Build query-string params from Refine CrudFilter[] for HROS BLL.
 *
 * The HROS BLL list endpoints accept flat query params (search, department_id,
 * status, employee_id, etc.) — equality filters map directly to param names.
 */
function buildHROSQueryParams(
  filters?: Filter[],
  meta?: AxonMeta,
): Record<string, string> {
  const params: Record<string, string> = {};

  if (meta?.search) {
    params['search'] = String(meta.search);
  }

  if (!filters || filters.length === 0) return params;

  for (const filter of filters) {
    if ('field' in filter && filter.operator === 'eq' && filter.value !== undefined && filter.value !== '') {
      params[filter.field] = String(filter.value);
    }
  }

  return params;
}

/**
 * createHROSDataProvider — Refine DataProvider for HROS BLL REST endpoints.
 *
 * Routes CRUD operations to /v1/hros/{resource} using standard REST verbs:
 *   GET    /v1/hros/{resource}?page=N&limit=N&search=X  — list
 *   GET    /v1/hros/{resource}/{id}                     — getOne
 *   POST   /v1/hros/{resource}                          — create
 *   PUT    /v1/hros/{resource}/{id}                     — update
 *   DELETE /v1/hros/{resource}/{id}                     — deleteOne
 *
 * Response envelope: { success, data, error, meta: { total, page, limit } }
 */
export function createHROSDataProvider(gatewayUrl: string): DataProvider {
  const httpClient = createAxiosInstance(gatewayUrl);

  return {
    getList: async <TData extends BaseRecord = BaseRecord>({
      resource,
      pagination,
      filters,
      sorters,
      meta,
    }: GetListParams): Promise<GetListResponse<TData>> => {
      const { current = 1, pageSize = 25 } = pagination ?? {};
      const axonMeta = meta as AxonMeta | undefined;
      const path = resolveHROSPath(resource);

      const params: Record<string, string | number> = {
        page: current,
        limit: pageSize,
      };

      // Sorting — pass first sorter as sort/order params
      if (sorters && sorters.length > 0) {
        params['sort'] = sorters[0].field;
        params['order'] = sorters[0].order;
      }

      // Flatten equality filters + search into query params
      const filterParams = buildHROSQueryParams(filters as Filter[], axonMeta);
      Object.assign(params, filterParams);

      const { data: response } = await httpClient.get<ApiResponse<TData[]>>(
        `/v1/hros/${path}`,
        { params },
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to fetch records',
          statusCode: 400,
        } as HttpError;
      }

      return {
        data: response.data ?? [],
        total: response.meta?.total ?? (response.data?.length ?? 0),
      };
    },

    getOne: async <TData extends BaseRecord = BaseRecord>({
      resource,
      id,
    }: GetOneParams): Promise<GetOneResponse<TData>> => {
      const path = resolveHROSPath(resource);

      const { data: response } = await httpClient.get<ApiResponse<TData>>(
        `/v1/hros/${path}/${id}`,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? `Record ${id} not found`,
          statusCode: 404,
        } as HttpError;
      }

      return { data: response.data };
    },

    create: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
      resource,
      variables,
    }: CreateParams<TVariables>): Promise<CreateResponse<TData>> => {
      const path = resolveHROSPath(resource);

      const { data: response } = await httpClient.post<ApiResponse<TData>>(
        `/v1/hros/${path}`,
        variables,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to create record',
          statusCode: 400,
        } as HttpError;
      }

      return { data: response.data };
    },

    update: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
      resource,
      id,
      variables,
    }: UpdateParams<TVariables>): Promise<UpdateResponse<TData>> => {
      const path = resolveHROSPath(resource);

      const { data: response } = await httpClient.put<ApiResponse<TData>>(
        `/v1/hros/${path}/${id}`,
        variables,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to update record',
          statusCode: 400,
        } as HttpError;
      }

      return { data: response.data };
    },

    deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
      resource,
      id,
    }: DeleteOneParams<TVariables>): Promise<DeleteOneResponse<TData>> => {
      const path = resolveHROSPath(resource);

      const { data: response } = await httpClient.delete<ApiResponse<unknown>>(
        `/v1/hros/${path}/${id}`,
      );

      if (!response.success) {
        throw {
          message: response.error?.message ?? 'Failed to delete record',
          statusCode: 400,
        } as HttpError;
      }

      return { data: { id } as unknown as TData };
    },

    getMany: async <TData extends BaseRecord = BaseRecord>({
      resource,
      ids,
    }: GetManyParams): Promise<GetManyResponse<TData>> => {
      const path = resolveHROSPath(resource);

      // Fetch each by ID in parallel — HROS BLL has individual getOne endpoints
      const results = await Promise.all(
        ids.map((id) =>
          httpClient
            .get<ApiResponse<TData>>(`/v1/hros/${path}/${id}`)
            .then((r) => r.data.data)
            .catch(() => null),
        ),
      );

      return { data: results.filter(Boolean) as TData[] };
    },

    getApiUrl: () => gatewayUrl,

    custom: async ({ url, method = 'get', payload, query, headers }) => {
      const { data } = await httpClient({
        url,
        method,
        data: payload,
        params: query,
        headers,
      });

      return { data };
    },
  };
}
